import { SkyDialog, SkyInput, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { localDateIso, resyncSentence, sinceHelp } from "@/components/runs/tableRuns";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useStartRun } from "@/hooks/use-runs";
import { newIdempotencyKey } from "@/lib/api";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { absolutePath } from "@/lib/routes";

/**
 * One table of a connection, run on its own (product gaps row 2, design section 4.5 point 4):
 *
 * - `full`, "Re-sync this table": every row of that table is read again and the target table is
 *   replaced (INSERT OVERWRITE). Other tables are not touched. A modal, because it is one
 *   destructive decision (rules.md §5), and the sentence says what is overwritten.
 * - `since`, "Read changes since...": the table's rows changed since a date are read and merged.
 *   The table's own watermark never moves back.
 *
 * Both are `POST /runs` with `tables` for this one table, under `runs.start`, and go to the new
 * run's page. Mechanics as `RunNowDialog`: one Idempotency-Key per open, a refusal stays inside.
 */

export interface TableRunDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "full" | "since";
  dataflowId: string;
  connectionName: string;
  table: string;
}

export function TableRunDialog({
  open,
  onOpenChange,
  mode,
  dataflowId,
  connectionName,
  table,
}: TableRunDialogProps) {
  const [, navigate] = useLocation();
  const notice = useNotice();
  const inline = useInlineError();
  const startRun = useStartRun();
  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);
  const inFlight = useRef(false);
  const [since, setSince] = useState("");
  // #1078: the date starts at midnight in the reader's display zone, which the start sends.
  const zone = useDisplayTz();
  const today = localDateIso(new Date(), zone);
  const ready = mode === "full" || (since !== "" && since <= today && since >= "1900-01-01");

  const submit = async (): Promise<void> => {
    if (inFlight.current || !ready) return;
    inFlight.current = true;
    inline.clear();
    try {
      const result = await startRun.mutateAsync({
        body: {
          dataflowId,
          tables:
            mode === "full"
              ? { mode: "full", entityNames: [table] }
              : { mode: "since", since, sinceTimeZone: zone, entityNames: [table] },
        },
        idempotencyKey,
      });
      const runPage = `/runs/${result.runId}`;
      onOpenChange(false);
      notice.success(
        mode === "full"
          ? `A re-sync of ${table} of ${connectionName} is queued.`
          : `An import of ${table} of ${connectionName} from ${since} is queued.`,
        { forPath: runPage },
      );
      navigate(absolutePath(runPage));
    } catch (error) {
      inline.show(error, "The import could not be started.");
    } finally {
      inFlight.current = false;
    }
  };

  const primary = mode === "full" ? `Re-sync ${table}` : "Read changes";
  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      title={mode === "full" ? `Re-sync ${table}?` : `Read changes of ${table} since a date`}
      size="medium"
      testId="table-run-dialog"
      onBeforeClose={(reason) => reason !== "primaryAction"}
      primaryAction={{
        label: startRun.isPending ? "Starting…" : primary,
        onClick: submit,
        disabled: startRun.isPending || !ready,
        loading: startRun.isPending,
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-4">
        {mode === "full" ? (
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            title="Every row of this table is replaced"
            message={resyncSentence(table, "the destination")}
            testId="table-run-overwrite"
          />
        ) : (
          <SkyInput
            type="date"
            label="Read changes since"
            required
            value={since}
            min="1900-01-01"
            max={today}
            hint={sinceHelp(zone)}
            onChange={(event) => setSince(event.target.value)}
            testId="table-run-since"
          />
        )}
        <SkyText size={200} color="muted" block>
          Only {table} is imported. It runs as a new import of {connectionName}, recorded in the
          audit trail against your account.
        </SkyText>
        <InlineErrorBar
          error={inline.error}
          title={inline.error?.retryable ? undefined : "The import could not be started"}
          testId="table-run-error"
        />
      </div>
    </SkyDialog>
  );
}
